import React, { useEffect, useMemo, useState } from 'react';
import { addServers } from '../lib/api';

// Simple validators
const ipv4Pattern = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const portPattern = /^([0-9]{1,5})$/;

function isIpLike(val) {
  if (!val) return false;
  const s = String(val).trim();
  // accept IPv4 or IPv4:port
  if (s.includes(':')) {
    const [ip, port] = s.split(':');
    if (!ipv4Pattern.test(ip)) return false;
    if (!portPattern.test(port)) return false;
    const p = Number(port);
    return p >= 1 && p <= 65535;
  }
  return ipv4Pattern.test(s);
}

export default function AddServers({ modalOnly = false, onClose }) {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // derived data
  const cleaned = useMemo(() => rows.map(r => (r || '').trim()), [rows]);
  const nonEmpty = useMemo(() => cleaned.filter(Boolean), [cleaned]);
  const invalidIndexes = useMemo(() => cleaned.map((v, i) => (v && !isIpLike(v) ? i : -1)).filter(i => i >= 0), [cleaned]);

  useEffect(() => {
    // Close on Escape
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Notify parent when modal closes in modalOnly mode
  useEffect(() => {
    if (modalOnly && !open && typeof onClose === 'function') {
      onClose();
    }
  }, [modalOnly, open, onClose]);

  const addRow = () => {
    setRows(prev => (prev.length >= 20 ? prev : [...prev, '']));
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, value) => {
    setRows(prev => prev.map((v, i) => (i === idx ? value : v)));
  };

  const canSend = nonEmpty.length > 0 && invalidIndexes.length === 0 && !submitting;

  const onSubmit = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await addServers(nonEmpty);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed: ${res.status}`);
      }
      setSuccess('Servers submitted successfully.');
      setOpen(false);
      // Optional: reset rows
      setRows(['']);
    } catch (e) {
      setError(e.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // If used as modalOnly and modal is closed, render nothing.
  if (modalOnly && !open) return null;

  return (
    <>
      {!modalOnly && (
        <div className="container">
          {/* Page header and helper text */}
          <div className="searchbar" style={{ justifyContent: 'space-between' }}>
            <h1 style={{ margin: 0 }}>Add Servers</h1>
            <button type="button" className="filter-toggle" onClick={() => setOpen(true)}>
              <i className="fa-solid fa-plus" aria-hidden="true" /> Add servers
            </button>
          </div>
          <p style={{ color: 'var(--muted)', marginTop: 8 }}>Add up to 20 server IPs (IPv4 or IPv4:port) and send them to the backend.</p>

          {success && (
            <div style={{ color: '#4CAF50', marginTop: 8 }}>{success}</div>
          )}
          {error && (
            <div style={{ color: '#f87171', marginTop: 8 }}>Error: {error}</div>
          )}
        </div>
      )}

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-servers-title" onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}>
          <div className="modal-dialog" role="document" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2 id="add-servers-title">Add servers</h2>
              <button type="button" className="modal-close" aria-label="Close" onClick={() => setOpen(false)}>
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="modal-body" style={{ padding: '8px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ color: 'var(--muted)' }}>Enter one IP per row. Maximum 6 servers.</div>
                <button type="button" className="btn-plain" onClick={addRow} disabled={rows.length >= 6}>
                  <i className="fa-solid fa-plus" aria-hidden="true" /> Add Server ({rows.length}/6)
                </button>
              </div>

              <div>
                {rows.map((val, idx) => {
                  const isInvalid = val && !isIpLike(val);
                  return (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <input
                        type="text"
                        className="search-input"
                        placeholder="e.g., 192.168.1.10 or 192.168.1.10:27015"
                        value={val}
                        onChange={(e) => updateRow(idx, e.target.value)}
                        aria-label={`Server ${idx + 1}`}
                        style={{ flex: 1, borderColor: isInvalid ? '#f87171' : undefined }}
                      />
                      <button type="button" className="btn-plain" onClick={() => removeRow(idx)} aria-label={`Remove row ${idx + 1}`}>
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {invalidIndexes.length > 0 && (
                <div style={{ color: '#f87171', marginTop: 6 }}>
                  Invalid IP format in row{invalidIndexes.length > 1 ? 's' : ''}: {invalidIndexes.map(i => i + 1).join(', ')}
                </div>
              )}
            </div>

            <div className="modal-actions" style={{ padding: '12px 16px' }}>
              <button type="button" className="btn-plain" onClick={() => setOpen(false)} disabled={submitting}>Cancel</button>
              <button type="button" className="filter-apply" onClick={onSubmit} disabled={!canSend}>
                {submitting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
